<!-- Adapted from YesPlayMusic v0.4.11 (MIT), upstream TrackList.vue and ContextMenu.vue.
     Track presentation and context-menu styling are retained; canonical data uses the shared runtime. -->
<template>
  <div class="track-list" :class="{'salt-track-grid':workspace.appearance==='salt' && type==='tracklist'}">
    <div class="context-menu">
      <TrackContextMenu v-if="rightClickedTrack" :key="rightClickedTrack.id" ref="menu" :track="rightClickedTrack" :playlist-context="type==='playlist'" :source-playlist-id="String(id)" :extras="extraContextMenuItem" @action="perform" @close="closeMenu" />
    </div>
    <div ref="items" :style="listStyles" class="track-list-items" :class="{'virtual-track-list': isVirtualized}">
      <TrackListItem
        v-for="entry in visibleEntries"
        :key="itemKey === 'id' ? entry.track.id : `${entry.track.id}${entry.index}`"
        :data-track-index="entry.index"
        :track-prop="entry.track"
        :track-no="entry.index + 1"
        :highlight-playing-track="highlightPlayingTrack"
        @play="playThisList(entry.track.id)"
        @menu="openMenu($event, entry.track, entry.index)"
        @dblclick.native="playThisList(entry.track.id)"
        @contextmenu.native.prevent="openMenu($event, entry.track, entry.index)"
      />
    </div>
  </div>
</template>

<script>
import TrackListItem from './TrackListItem.vue';
import { runtime } from '../bridge';
import { workspace } from '../workspace';
import { openDownloads } from '../downloads';
import { trackMetadataEditCapability } from '../track-metadata-edit';
import { shareTrack } from '../share-track';
import { openPlaylistTarget } from '../playlist-target';
import TrackContextMenu from './TrackContextMenu.vue';

const VIRTUAL_TRACK_THRESHOLD = 120;
const VIRTUAL_OVERSCAN_ROWS = 2;
const INITIAL_VIRTUAL_ITEMS = 64;

export default {
  name: 'TrackList',
  components: { TrackListItem, TrackContextMenu },
  props: {
    tracks: { type: Array, default: () => [] },
    type: { type: String, default: 'tracklist' },
    id: { type: [Number, String], default: '' },
    albumObject: { type: Object, default: () => ({artist: {name: ''}}) },
    extraContextMenuItem: { type: Array, default: () => [] },
    columnNumber: { type: Number, default: 4 },
    highlightPlayingTrack: { type: Boolean, default: true },
    itemKey: { type: String, default: 'id' },
    preservePlaybackContext: { type: Boolean, default: false },
    loadPlaybackTracks: { type: Function, default: null },
  },
  data() {
    return {
      state: runtime.state,
      workspace,
      rightClickedTrack: null,
      rightClickedTrackIndex: -1,
      virtualStart: 0,
      virtualEnd: INITIAL_VIRTUAL_ITEMS,
      virtualTop: 0,
      virtualBottom: 0,
      virtualColumns: 1,
      virtualRowHeight: 52,
      virtualRowGap: 4,
    };
  },
  computed: {
    isVirtualized() {
      return this.tracks.length > VIRTUAL_TRACK_THRESHOLD;
    },
    visibleEntries() {
      const start = this.isVirtualized ? this.virtualStart : 0;
      const end = this.isVirtualized ? Math.min(this.tracks.length, this.virtualEnd) : this.tracks.length;
      return this.tracks.slice(start, end).map((track, offset) => ({ track, index: start + offset }));
    },
    listStyles() {
      const styles = this.type === 'tracklist' ? {
        display: 'grid', gap: '4px', gridTemplateColumns: `repeat(${this.columnNumber}, minmax(0, 1fr))`,
      } : {};
      if (this.isVirtualized) Object.assign(styles, {
        boxSizing: 'border-box',
        overflowAnchor: 'none',
        paddingTop: `${this.virtualTop}px`,
        paddingBottom: `${this.virtualBottom}px`,
      });
      return styles;
    },
  },
  watch: {
    tracks() { this.onTracksChanged(); },
    type() { this.onTracksChanged(); },
    columnNumber() { this.onTracksChanged(); },
  },
  mounted() {
    this._scrollTarget = null;
    this._virtualFrame = 0;
    document.addEventListener('pointerdown', this.onOutsidePointer);
    window.addEventListener('resize', this.onResize);
    document.addEventListener('scroll', this.onScroll, true);
    this.$nextTick(() => { this.bindScrollTarget(); this.refreshVirtualWindow(); });
  },
  activated() {
    this.$nextTick(() => { this.bindScrollTarget(); this.refreshVirtualWindow(); });
  },
  deactivated() {
    this.unbindScrollTarget();
    this.closeMenu();
  },
  beforeDestroy() {
    this._playRequest = (this._playRequest || 0) + 1;
    this.unbindScrollTarget();
    if (this._virtualFrame) cancelAnimationFrame(this._virtualFrame);
    document.removeEventListener('pointerdown', this.onOutsidePointer);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('scroll', this.onScroll, true);
  },
  methods: {
    cover: runtime.cover,
    onTracksChanged() {
      if (!this.isVirtualized) {
        this.virtualStart = 0;
        this.virtualEnd = this.tracks.length;
        this.virtualTop = 0;
        this.virtualBottom = 0;
      } else {
        if (this.virtualStart >= this.tracks.length) {
          this.virtualStart = 0;
          this.virtualEnd = Math.min(this.tracks.length, INITIAL_VIRTUAL_ITEMS);
        }
        // Set spacers before Vue commits the shorter virtual window. Otherwise
        // crossing the threshold briefly collapses the list and clamps scrollTop.
        this.refreshVirtualWindow();
      }
      this.$nextTick(() => { this.bindScrollTarget(); this.scheduleVirtualRefresh(); });
    },
    findScrollTarget() {
      let element = this.$el?.parentElement;
      while (element && element !== document.body && element !== document.documentElement) {
        const style = getComputedStyle(element);
        if (/(auto|scroll|overlay)/.test(`${style.overflowY} ${style.overflow}`)) return element;
        element = element.parentElement;
      }
      return window;
    },
    bindScrollTarget() {
      const target = this.findScrollTarget();
      if (target === this._scrollTarget) return;
      this.unbindScrollTarget();
      this._scrollTarget = target;
      this._scrollTarget.addEventListener('scroll', this.scheduleVirtualRefresh, { passive: true });
    },
    unbindScrollTarget() {
      this._scrollTarget?.removeEventListener('scroll', this.scheduleVirtualRefresh);
      this._scrollTarget = null;
    },
    scheduleVirtualRefresh() {
      if (!this.isVirtualized || this._virtualFrame) return;
      this._virtualFrame = requestAnimationFrame(() => {
        this._virtualFrame = 0;
        this.refreshVirtualWindow();
      });
    },
    flushVirtualRefresh() {
      if (this._virtualFrame) cancelAnimationFrame(this._virtualFrame);
      this._virtualFrame = 0;
      this.refreshVirtualWindow();
    },
    async revealVirtualIndex(index) {
      if (!this.isVirtualized || index < 0 || index >= this.tracks.length || !this.$refs.items) return null;
      const target = this._scrollTarget || this.findScrollTarget();
      const listRect = this.$refs.items.getBoundingClientRect();
      const stride = Math.max(1, this.virtualRowHeight + this.virtualRowGap);
      const rowOffset = Math.floor(index / this.virtualColumns) * stride;
      if (target === window) {
        window.scrollTo({ top: Math.max(0, window.scrollY + listRect.top + rowOffset - window.innerHeight / 2), behavior: 'auto' });
      } else {
        const targetRect = target.getBoundingClientRect();
        target.scrollTop = Math.max(0, target.scrollTop + listRect.top - targetRect.top + rowOffset - target.clientHeight / 2);
      }
      this.flushVirtualRefresh();
      await this.$nextTick();
      return this.$refs.items?.querySelector(`[data-track-index="${index}"]`) || null;
    },
    refreshVirtualWindow() {
      if (!this.isVirtualized) return;
      const list = this.$refs.items;
      // A closed search/section has no measurable rows. Keep its last valid
      // spacers instead of inferring columns from zero-sized hidden elements.
      if (!list || !list.getClientRects().length) return;
      const rows = Array.from(list.querySelectorAll(':scope > [data-track-row]'));
      const listStyle = getComputedStyle(list);
      let columns = this.type === 'tracklist' ? Math.max(1, Number(this.columnNumber) || 1) : 1;
      if (rows.length > 1) {
        const firstTop = rows[0].offsetTop;
        const measuredColumns = rows.findIndex((row) => Math.abs(row.offsetTop - firstTop) > 1);
        columns = measuredColumns > 0 ? measuredColumns : Math.min(columns, rows.length);
      }
      const measuredHeight = rows[0]?.getBoundingClientRect().height;
      const rowHeight = measuredHeight > 0 ? measuredHeight : this.virtualRowHeight;
      const rowGap = Number.parseFloat(listStyle.rowGap) || 0;
      const target = this._scrollTarget || this.findScrollTarget();
      const listRect = list.getBoundingClientRect();
      let scrollTop;
      let viewportHeight;
      let listTop;
      if (target === window) {
        scrollTop = window.scrollY || document.documentElement.scrollTop;
        viewportHeight = window.innerHeight;
        listTop = listRect.top + scrollTop;
      } else {
        const targetRect = target.getBoundingClientRect();
        scrollTop = target.scrollTop;
        viewportHeight = target.clientHeight;
        listTop = listRect.top - targetRect.top + scrollTop;
      }
      const stride = Math.max(1, rowHeight + rowGap);
      const totalRows = Math.ceil(this.tracks.length / columns);
      const visibleStart = Math.max(0, scrollTop - listTop);
      const visibleEnd = Math.max(visibleStart, scrollTop + viewportHeight - listTop);
      const startRow = Math.min(totalRows - 1, Math.max(0, Math.floor(visibleStart / stride) - VIRTUAL_OVERSCAN_ROWS));
      const endRow = Math.min(totalRows, Math.max(startRow + 1, Math.ceil(visibleEnd / stride) + VIRTUAL_OVERSCAN_ROWS));
      const start = startRow * columns;
      const end = Math.min(this.tracks.length, endRow * columns);
      const renderedRows = Math.ceil((end - start) / columns);
      const totalHeight = totalRows * rowHeight + Math.max(0, totalRows - 1) * rowGap;
      const renderedHeight = renderedRows * rowHeight + Math.max(0, renderedRows - 1) * rowGap;
      this.virtualColumns = columns;
      this.virtualRowHeight = rowHeight;
      this.virtualRowGap = rowGap;
      this.virtualStart = start;
      this.virtualEnd = end;
      this.virtualTop = startRow * stride;
      this.virtualBottom = Math.max(0, totalHeight - this.virtualTop - renderedHeight);
    },
    onResize() {
      this.closeMenu();
      this.$nextTick(() => { this.bindScrollTarget(); this.scheduleVirtualRefresh(); });
    },
    async playThisList(trackId) {
      const ticket = this._playRequest = (this._playRequest || 0) + 1;
      let playbackTicket = null;
      try {
        if (this.preservePlaybackContext) {
          const selected = this.tracks.find(track => track.id === trackId);
          if (selected) runtime.registerTracks([selected]);
          await runtime.play(trackId);
        }
        else {
          const load = this.loadPlaybackTracks;
          playbackTicket = load ? runtime.reservePlaybackRequest() : null;
          const tracks = load ? await load() : this.tracks;
          if (ticket !== this._playRequest || load && !runtime.isPlaybackRequestCurrent(playbackTicket)) return;
          await runtime.playTracks(tracks, trackId);
        }
      }
      catch (error) { if (ticket === this._playRequest && (playbackTicket === null || runtime.isPlaybackRequestCurrent(playbackTicket)) && error.name !== 'AbortError') runtime.notify(error.message || '播放失败，请稍后重试', 'error'); }
    },
    openMenu(event, track, index = -1) {
      event.preventDefault();
      event.stopPropagation();
      this._menuOpener = event.currentTarget?.matches('button') ? event.currentTarget : event.currentTarget?.querySelector('[aria-haspopup="menu"]');
      // Browsers can deliver the final scroll-into-view event after the click
      // handler. Keep that same interaction from closing the menu immediately.
      this._menuScrollGuardUntil = performance.now() + 250;
      this.rightClickedTrack = track;
      this.rightClickedTrackIndex = index;
      const rect = event.currentTarget?.getBoundingClientRect();
      const x = event.type === 'contextmenu' ? event.clientX : (rect?.left || event.clientX);
      const y = event.type === 'contextmenu' ? event.clientY : (rect?.bottom || event.clientY);
      this.$nextTick(() => {
        const menu = this.$refs.menu;
        if (!menu) return;
        menu.place(x, y);
      });
    },
    closeMenu(restoreFocus = false) {
      this.rightClickedTrack = null;
      this.rightClickedTrackIndex = -1;
      if (restoreFocus && this._menuOpener?.isConnected) this._menuOpener.focus({ preventScroll: true });
    },
    onOutsidePointer(event) {
      if (this.rightClickedTrack && !this.$refs.menu?.$el.contains(event.target)) this.closeMenu();
    },
    onScroll(event) {
      if (this.rightClickedTrack && performance.now() >= (this._menuScrollGuardUntil || 0) && !this.$refs.menu?.$el.contains(event.target)) this.closeMenu();
    },
    async perform(action, value) {
      const track = this.rightClickedTrack;
      if (!track) return;
      if (action === 'edit' && !trackMetadataEditCapability(track).editable) return runtime.notify(trackMetadataEditCapability(track).reason, 'info');
      this.closeMenu(true);
      try {
        if (action === 'share') { const result = await shareTrack(track); if (result === 'copied') runtime.notify('歌曲链接已复制', 'success'); return; }
        if (action === 'move') return runtime.movePlaylistTracks([track.id], String(this.id), value);
        runtime.registerTracks([track]);
        if (action === 'play') await this.playThisList(track.id);
        if (action === 'next') runtime.playNext(track.id);
        if (action === 'queue') runtime.enqueue(track.id);
        if (action === 'download') openDownloads([track], track.title, value && value!=='original' ? {quality:value} : {});
        if (action === 'add') runtime.addToPlaylist(track.id, value);
        if (action === 'create') openPlaylistTarget(track, this._menuOpener);
        if (action === 'favorite') runtime.toggleFavorite(track.id);
        if (action === 'remove') this.$emit('remove-track', track.id);
        if (action === 'edit') this.$emit('edit-track', track.id);
        if (action === 'details') this.$emit('track-details', track.id);
        if (action === 'copy') this.$emit('copy-track', track.id);
        if (action === 'bindLyrics') this.$emit('bind-lyrics', track.id);
        if (action === 'clearCache') this.$emit('clear-track-cache', track.id);
      } catch (error) { runtime.notify(error.message || '操作失败', 'error'); }
    },
  },
};
</script>

<style lang="scss" scoped>
.context-menu { user-select:none; }
@media (max-width:720px) { .track-list-items { grid-template-columns:minmax(0,1fr) !important; } }
.salt-track-grid .track-list-items { grid-template-columns:repeat(auto-fill,minmax(min(100%,280px),1fr)) !important;gap:4px 12px !important; }
</style>
